/**
 * Gráficos da evolução do sistema, na moldura comum das fichas
 * (`ui/GraficoTempo`): a área, as temperaturas mínima e média e a expansão,
 * empilhadas sobre um eixo de tempo só, com o cursor em comum.
 *
 * O desenho fica parado, com a história mais longa que se conhece do sistema;
 * o que anda com o player é a marca do instante, como nas outras fichas.
 * As peças (`Faixa`, `Linhas`, `Valor`) servem também à ficha da chuva.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { formatDecimal, formatInt } from '../lib/format';
import { formatTimeBrt } from '../lib/time';
import { centroPct } from '../ui/fatias';
import { EixoTempo, GRAFICO_A, GraficoTempo } from '../ui/GraficoTempo';
import { faixa, trechos, type SerieRegular } from './evolucao';

export const COR_AREA = '#ffd84a';
export const COR_TMIN = '#7fe3ff';
export const COR_TMED = '#ffb27a';
const COR_CRESCE = '#34d399';
const COR_ENCOLHE = '#ff5d73';

export interface Cursor {
  /** Fatia do quadro exibido; -1 quando ele está fora da história. */
  iAtual: number;
  sob: number | null;
  aoSob: (i: number | null) => void;
}

interface Linha {
  serie: readonly (number | null)[];
  cor: string;
  /** Área preenchida sob a linha. */
  cheia?: boolean;
}

export function Linhas(props: { linhas: Linha[]; instantes: readonly number[]; piso: number; rotulo: string; altura: number; cursor: Cursor }) {
  const { linhas, instantes } = props;
  const n = instantes.length;
  const f = useMemo(() => faixa(linhas.map((l) => l.serie), props.piso), [linhas, props.piso]);
  const partes = useMemo(() => linhas.map((l) => trechos(l.serie)), [linhas]);
  if (!f) return <div className="text-[11px] text-ink-400">Sem valores.</div>;

  const A = GRAFICO_A;
  const FOLGA = 3;
  const y = (v: number) => A - FOLGA - ((v - f.base) / f.amplitude) * (A - 2 * FOLGA);

  return (
    <GraficoTempo
      instantes={instantes}
      iAtual={props.cursor.iAtual}
      rotulo={props.rotulo}
      valor={() => null}
      resumo={null}
      soDesenho
      altura={props.altura}
      sob={props.cursor.sob}
      aoSob={props.cursor.aoSob}
      desenho={({ w, sob }) => (
        <>
          {sob !== null && (
            <line x1={(sob + 0.5) * w} y1="0" x2={(sob + 0.5) * w} y2={A} stroke="rgba(255,255,255,0.35)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          )}
          {linhas.map((l, k) =>
            partes[k]!.map((t) => {
              if (t.length === 1) {
                return (
                  <line
                    key={`${k}-${t[0]}`}
                    x1={(t[0]! + 0.2) * w}
                    x2={(t[0]! + 0.8) * w}
                    y1={y(l.serie[t[0]!]!)}
                    y2={y(l.serie[t[0]!]!)}
                    stroke={l.cor}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                );
              }
              const pontos = t.map((i) => `${((i + 0.5) * w).toFixed(2)},${y(l.serie[i]!).toFixed(2)}`).join(' ');
              return (
                <g key={`${k}-${t[0]}`}>
                  {l.cheia && (
                    <polygon
                      points={`${((t[0]! + 0.5) * w).toFixed(2)},${A} ${pontos} ${((t[t.length - 1]! + 0.5) * w).toFixed(2)},${A}`}
                      fill={l.cor}
                      opacity="0.14"
                    />
                  )}
                  <polyline points={pontos} fill="none" stroke={l.cor} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                </g>
              );
            }),
          )}
        </>
      )}
      sobre={({ destaque }) => (
        <>
          {/* Pontos em HTML: o `preserveAspectRatio="none"` faria deles elipses. */}
          {destaque !== null &&
            linhas.map((l, k) => {
              const v = l.serie[destaque];
              if (typeof v !== 'number') return null;
              return (
                <span
                  key={k}
                  aria-hidden
                  className="pointer-events-none absolute size-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-ink-900"
                  style={{ left: `${centroPct(destaque, n)}%`, top: `${(y(v) / A) * 100}%`, background: l.cor }}
                />
              );
            })}
        </>
      )}
    />
  );
}

/** Expansão em barras: para cima (verde) a área cresce, para baixo (vermelho) encolhe. */
function Barras(props: { serie: readonly (number | null)[]; instantes: readonly number[]; rotulo: string; altura: number; cursor: Cursor }) {
  const { serie, instantes } = props;
  const maior = Math.max(50, ...serie.map((v) => (typeof v === 'number' ? Math.abs(v) : 0)));
  const A = GRAFICO_A;
  const meio = A / 2;
  return (
    <GraficoTempo
      instantes={instantes}
      iAtual={props.cursor.iAtual}
      rotulo={props.rotulo}
      valor={() => null}
      resumo={null}
      soDesenho
      altura={props.altura}
      sob={props.cursor.sob}
      aoSob={props.cursor.aoSob}
      desenho={({ w, destaque }) => (
        <>
          <line x1="0" y1={meio} x2={instantes.length * w} y2={meio} stroke="currentColor" strokeWidth="1" opacity="0.3" vectorEffect="non-scaling-stroke" />
          {serie.map((v, i) => {
            if (typeof v !== 'number' || v === 0) return null;
            const h = Math.max(1.2, (Math.abs(v) / maior) * (meio - 1.5));
            return (
              <rect
                key={i}
                x={i * w + 0.2}
                y={v > 0 ? meio - h : meio}
                width={Math.max(0.6, w - 0.4)}
                height={h}
                fill={v > 0 ? COR_CRESCE : COR_ENCOLHE}
                opacity={i === destaque ? 1 : 0.8}
              />
            );
          })}
        </>
      )}
    />
  );
}

/** Uma faixa: título, escala (mín–máx) e o valor da hora do cursor à direita. */
export function Faixa(props: { titulo: ReactNode; escala?: string | null; leitura: ReactNode; children: ReactNode }) {
  return (
    <div className="pt-1">
      <div className="flex items-baseline gap-1.5 text-[10px] leading-tight text-ink-400">
        <span className="min-w-0 truncate">{props.titulo}</span>
        {props.escala && <span className="shrink-0 tabular-nums">{props.escala}</span>}
        <span className="ml-auto shrink-0 text-xs leading-tight font-semibold whitespace-nowrap text-ink-100 tabular-nums">{props.leitura}</span>
      </div>
      {props.children}
    </div>
  );
}

export function minMax(formato: (v: number) => string, ...series: (readonly (number | null)[])[]): string | null {
  const m = series.flat().filter((v): v is number => typeof v === 'number');
  if (!m.length) return null;
  const lo = formato(Math.min(...m));
  const hi = formato(Math.max(...m));
  return lo === hi ? lo : `${lo}–${hi}`;
}

export function Valor(props: { v: number | null | undefined; formato: (v: number) => string; cor?: string }) {
  return typeof props.v === 'number' ? <span style={{ color: props.cor }}>{props.formato(props.v)}</span> : <span className="text-ink-400">—</span>;
}

const km2 = (v: number) => formatInt(v);
const kelvin = (v: number) => formatDecimal(v, 1);
const expansao = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatInt(Math.abs(v))}`;

/** A fatia de `instantes` (igualmente espaçados) que contém `t`, ou -1. */
export function fatiaDoInstante(instantes: readonly number[], t: number | null): number {
  const n = instantes.length;
  if (t === null || n < 2) return -1;
  const passo = (instantes[n - 1]! - instantes[0]!) / (n - 1);
  const i = Math.round((t - instantes[0]!) / passo);
  return i >= 0 && i < n && Math.abs(instantes[i]! - t) <= passo / 2 ? i : -1;
}

export function Evolucao(props: { serie: SerieRegular; /** Instante do quadro exibido (epoch ms). */ instante: number | null }) {
  const { serie } = props;
  const [sob, setSob] = useState<number | null>(null);
  const n = serie.instantes.length;
  const iAtual = fatiaDoInstante(serie.instantes, props.instante);
  const cursor: Cursor = { iAtual, sob, aoSob: setSob };
  // Sem o mouse, a leitura é a do quadro exibido; fora da história, a do fim dela.
  const i = sob ?? (iAtual >= 0 ? iAtual : n - 1);
  const temExp = serie.exp.some((v) => typeof v === 'number');
  const desde = formatTimeBrt(serie.instantes[0]!);
  const ate = formatTimeBrt(serie.instantes[n - 1]!);

  return (
    <div data-evolucao="">
      <Faixa titulo="Área (km²)" escala={minMax(km2, serie.km2)} leitura={<Valor v={serie.km2[i]} formato={km2} cor={COR_AREA} />}>
        <Linhas
          linhas={[{ serie: serie.km2, cor: COR_AREA, cheia: true }]}
          instantes={serie.instantes}
          piso={1}
          altura={44}
          cursor={cursor}
          rotulo={`Área do sistema das ${desde} às ${ate}, entre ${minMax(km2, serie.km2) ?? '—'} km²`}
        />
      </Faixa>
      <Faixa
        titulo={
          <>
            {/* Curto: em 360 px o título divide a linha com a escala e a leitura. */}
            Temp. (K): <span style={{ color: COR_TMIN }}>mín.</span> e <span style={{ color: COR_TMED }}>média</span>
          </>
        }
        leitura={
          <>
            <Valor v={serie.tmin[i]} formato={kelvin} cor={COR_TMIN} /> <span className="text-ink-500">/</span>{' '}
            <Valor v={serie.tmed[i]} formato={kelvin} cor={COR_TMED} />
          </>
        }
      >
        <Linhas
          linhas={[
            { serie: serie.tmed, cor: COR_TMED },
            { serie: serie.tmin, cor: COR_TMIN },
          ]}
          instantes={serie.instantes}
          piso={4}
          altura={48}
          cursor={cursor}
          rotulo={`Temperatura de brilho mínima (${minMax(kelvin, serie.tmin) ?? '—'} K) e média (${minMax(kelvin, serie.tmed) ?? '—'} K) das ${desde} às ${ate}`}
        />
      </Faixa>
      {temExp && (
        <Faixa titulo="Expansão da área (10⁻⁶ s⁻¹)" leitura={<Valor v={serie.exp[i]} formato={expansao} />}>
          <Barras
            serie={serie.exp}
            instantes={serie.instantes}
            altura={28}
            cursor={cursor}
            rotulo={`Expansão normalizada da área das ${desde} às ${ate}: barras para cima, o sistema cresce; para baixo, encolhe`}
          />
        </Faixa>
      )}
      <EixoTempo instantes={serie.instantes} perto={sob} />
      <div className="mt-0.5 text-[10px] text-ink-400 tabular-nums">
        {sob !== null ? (
          <span className="text-ink-200">{formatTimeBrt(serie.instantes[sob]!)}</span>
        ) : iAtual >= 0 ? (
          `imagem das ${formatTimeBrt(serie.instantes[iAtual]!)}`
        ) : (
          `a imagem exibida está fora desta história (até ${ate})`
        )}
      </div>
    </div>
  );
}
